import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Button, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbar,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { API } from "../../../api/API";
import { appColors } from "../../../constant/constant";
import formatDateTime from "../../../utils/dateFormate";
import DeleteDialog from "../modals/DeleteModal";
import EditContratModal from "../modals/EditContratModal";
import ProfilContrats from "../modals/ProfilContrats";
const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.success.light
    : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,

  borderRadius: "10px",
  padding: "4px 2px",
  height: "auto",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const StatusButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  fontWeight: 600,
  borderRadius: "16px",
  padding: "4px 12px",
  minWidth: "100px",
  fontSize: "0.75rem",
}));
const RenderStatus = ({ statut }) => {
  return (
    <StatusButton
      color="success"
      variant="contained"
      startIcon={<CheckCircleOutlineIcon fontSize="small" />}
      sx={{
        backgroundColor: "#4caf50",
        "&:hover": {
          backgroundColor: "#388e3c",
        },
      }}
    >
      Validé
    </StatusButton>
  );
};

const actionTypes = {
  voir: "voir",
  modifier: "modifier",
  supprimer: "supprimer",
};

const iconList = [
  <VisibilityOutlinedIcon sx={{ color: appColors.secondary }} />,
  <EditOutlinedIcon sx={{ color: appColors.secondary }} />,
  <DeleteOutlineOutlinedIcon sx={{ color: appColors.danger }} />,
];

const DataGridContrats = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [rows, setRows] = useState([]);

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  //const user = useAuthStore((state) => state.user);

  const handleAction = useCallback((selectedRow, actionType) => {
    setSelectedRow(selectedRow);
    switch (actionType) {
      case actionTypes.voir:
        setIsViewModalOpen(true);
        break;
      case actionTypes.modifier:
        setIsEditModalOpen(true);
        break;
      case actionTypes.supprimer:
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    try {
      const res = await API.delete(`/contrats-assurances/${selectedRow.id}`);
      if (res.status === 200 || res.status === 204) {
        toast.success("Contrat supprimé avec succès");
        handleRefresh();
        setIsDeleteModalOpen(false);
      }
    } catch (error) {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
      console.error(error.message);
      setIsDeleteModalOpen(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "order",
        headerName: "#",
        width: 60,
      },
      {
        field: "assure",
        headerName: "Assuré",
        width: 180,
        valueGetter: (value, r) => `${value.nom} ${value.prenom}`,
      },
      {
        field: "police",
        headerName: "Police",
        width: 150,
        valueGetter: (value, r) => `${value.label}`,
      },
      {
        field: "policeNumber", // Changed from "police" to "policeNumber"
        headerName: "N° Police",
        width: 150,
        valueGetter: (value, row) => `${row.police?.numero}`, // Updated to access police.numero
      },
      {
        field: "date_debut",
        headerName: "Date début",
        width: 120,
        valueGetter: (value, r) => formatDateTime(value),
      },
      {
        field: "date_fin",
        headerName: "Date fin",
        width: 120,
        valueGetter: (value, r) =>
          value ? formatDateTime(value) : "Indéterminée",
      },
      {
        field: "montant",
        headerName: "Montant",
        width: 120,
        valueGetter: (value, r) => (value ? `${value} FCFA` : "N/A"),
      },
      /*  {
        field: "statut",
        headerName: "Statut",
        width: 120,
        renderCell: (value, r) => <RenderStatus statut={value} />,
      },*/
      {
        field: "createdAt",
        headerName: "Créé le",
        width: 120,
        valueGetter: (value, r) => formatDateTime(value),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 100,
        disableExport: true,
        getActions: (params) =>
          Object.entries(actionTypes).map(([key, value], index) => (
            <GridActionsCellItem
              icon={iconList[index]}
              label={value}
              key={key}
              onClick={() => handleAction(params.row, value)}
              showInMenu
            />
          )),
      },
    ],
    [handleAction]
  );

  const fetchContrats = () => {
    setIsLoading(true);
    API.get("/contrats-assurances")
      .then((response) => {
        const data = response.data.data.map((row, index) => ({
          ...row,
          order: index + 1,
        }));

        setRows(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error.message);
        toast.error(`Erreur lors du chargement des contrats: ${error.message}`);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchContrats();
  }, []);

  const handleRefresh = () => {
    setIsLoading(true);
    fetchContrats();
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ backgroundColor: "#f5f5f5", color: "#37B049" }}>
      <GridToolbar
        sx={{
          "& .MuiButton-root": {
            color: "#37B049",
          },
          "& .MuiSvgIcon-root": {
            color: "#37B049",
          },
        }}
      />
      {/* Uncomment when AddContratModal is implemented */}
      {/* <AddContratModal onRefresh={handleRefresh} /> */}
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <ToastContainer />
      <DataGrid
        slots={{
          toolbar: CustomToolbar,
        }}
        columns={columns}
        rows={rows}
        checkboxSelection
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pagination.limit,
            },
          },
        }}
        pageSizeOptions={[pagination.limit]}
        loading={isLoading}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
      />

      {/* Uncomment when ViewContratModal is implemented */}
      {isViewModalOpen && selectedRow && (
        <ProfilContrats
          show={isViewModalOpen}
          handleClose={() => setIsViewModalOpen(false)}
          contrat={selectedRow}
        />
      )}

      {/* Uncomment when EditContratModal is implemented */}
      {isEditModalOpen && selectedRow && (
        <EditContratModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          contrat={selectedRow}
          onRefresh={handleRefresh}
        />
      )}

      {isDeleteModalOpen && selectedRow && (
        <DeleteDialog
          isDeleteDialogOpen={isDeleteModalOpen}
          setIsDeleteDialogOpen={() => setIsDeleteModalOpen(false)}
          handleDeleteConfirm={handleDeleteConfirm}
          message="Voulez-vous vraiment supprimer ce contrat d'assurance ?"
        />
      )}
    </Box>
  );
};

export default DataGridContrats;
